import * as React from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded'
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded'
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded'
import HandymanRoundedIcon from '@mui/icons-material/HandymanRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded'
import UnitIconFrame from './UnitIconFrame'

const PRODUCE_COMPONENTS_PLUGIN_KEYS = ['auto_craft_components', 'auto_craft_refinery']

const assetUrl = path => `/assets/${path}.png`

const RESOURCE_ICON_URLS = Object.freeze({
    refinery: assetUrl('itemassets/Building/Refinery/Refinery_Building_Level5/Refinery_Building_Level5--1720604187235'),
    toolsmith: assetUrl('itemassets/Building/Toolsmith/Toolsmith_Building_Level4/Toolsmith_Building_Level4--1623158072971'),
    dragonHoard: assetUrl('itemassets/Building/DragonHoard/DragonHoard_Building_Level5/DragonHoard_Building_Level5--1712658370052'),
    dragonBreathForge: assetUrl('itemassets/Building/DragonBreathForge/DragonBreathForge_Building_Level5/DragonBreathForge_Building_Level5--1718878430014'),
    refinedLumber: assetUrl('itemassets/Collectables/Collectable_Currency_RefinedLumber/Collectable_Currency_RefinedLumber--1623158072971'),
    refinedStone: assetUrl('itemassets/Collectables/Collectable_Currency_RefinedStone/Collectable_Currency_RefinedStone--1623158072971'),
    steel: assetUrl('itemassets/Collectables/Collectable_Currency_Steel/Collectable_Currency_Steel--1716552645290'),
    dragonGlass: assetUrl('itemassets/Collectables/Collectable_Currency_DragonGlass/Collectable_Currency_DragonGlass--1716552645290'),
    component1: assetUrl('itemassets/Collectables/Collectable_Currency_Component1/Collectable_Currency_Component1--1623158072971'),
    component2: assetUrl('itemassets/Collectables/Collectable_Currency_Component2/Collectable_Currency_Component2--1623158072971'),
    component3: assetUrl('itemassets/Collectables/Collectable_Currency_Component3/Collectable_Currency_Component3--1623158072971'),
    component4: assetUrl('itemassets/Collectables/Collectable_Currency_Component4/Collectable_Currency_Component4--1623158072971'),
    component5: assetUrl('itemassets/Collectables/Collectable_Currency_Component5/Collectable_Currency_Component5--1623158072971'),
    component6: assetUrl('itemassets/Collectables/Collectable_Currency_Component6/Collectable_Currency_Component6--1623158072971'),
    component7: assetUrl('itemassets/Collectables/Collectable_Currency_Component7/Collectable_Currency_Component7--1623158072971'),
    component8: assetUrl('itemassets/Collectables/Collectable_Currency_Component8/Collectable_Currency_Component8--1623158072971'),
    dragonScaleTile: assetUrl('itemassets/Collectables/Collectable_Currency_DragonScaleTile/Collectable_Currency_DragonScaleTile--1712658370052'),
    dragonCharm: assetUrl('itemassets/Collectables/Collectable_Currency_DragonCharm/Collectable_Currency_DragonCharm--1716552645290'),
    twinFlameAxes: assetUrl('itemassets/Collectables/Collectable_Currency_TwinFlameAxes/Collectable_Currency_TwinFlameAxes--1716552645290'),
    dragonGlassArrows: assetUrl('itemassets/Collectables/Collectable_Currency_DragonGlassArrows/Collectable_Currency_DragonGlassArrows--1716552645290'),
    dragonScaleArmor: assetUrl('itemassets/Collectables/Collectable_Currency_DragonScaleArmor/Collectable_Currency_DragonScaleArmor--1716552645290'),
    dragonScaleArrows: assetUrl('itemassets/Collectables/Collectable_Currency_DragonScaleArrows/Collectable_Currency_DragonScaleArrows--1716552645290')
})

const RECIPE_ICON_URL_BY_ITEM = Object.freeze({
    'Refined wood': RESOURCE_ICON_URLS.refinedLumber,
    'Refined stone': RESOURCE_ICON_URLS.refinedStone,
    Iron: RESOURCE_ICON_URLS.steel,
    Glass: RESOURCE_ICON_URLS.dragonGlass,
    Screw: RESOURCE_ICON_URLS.component1,
    'Black Powder': RESOURCE_ICON_URLS.component2,
    Saw: RESOURCE_ICON_URLS.component3,
    Drill: RESOURCE_ICON_URLS.component4,
    Crowbar: RESOURCE_ICON_URLS.component5,
    'Leather Strip': RESOURCE_ICON_URLS.component6,
    Chain: RESOURCE_ICON_URLS.component7,
    'Metal Plate': RESOURCE_ICON_URLS.component8,
    'Dragon Scale Tile': RESOURCE_ICON_URLS.dragonScaleTile,
    'Dragon Charm': RESOURCE_ICON_URLS.dragonCharm,
    'Twin Flame Axes': RESOURCE_ICON_URLS.twinFlameAxes,
    'Dragon Glass Arrows': RESOURCE_ICON_URLS.dragonGlassArrows,
    'Dragon Scale Armor': RESOURCE_ICON_URLS.dragonScaleArmor,
    'Dragon Scale Arrows': RESOURCE_ICON_URLS.dragonScaleArrows
})

const cloneValue = value => {
    if (typeof structuredClone === 'function')
        return structuredClone(value)

    return JSON.parse(JSON.stringify(value ?? {}))
}

const toStringValue = (value, fallback = '') =>
    [undefined, null].includes(value) ? fallback : String(value)

const normalizeSearchText = value =>
    String(value ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')

const PICKER_FAVORITES_KEY = 'produceComponentsRecipePickerFavorites'

const loadPickerFavorites = () => {
    if (typeof window === 'undefined')
        return []

    try {
        const parsed = JSON.parse(window.localStorage.getItem(PICKER_FAVORITES_KEY) ?? '[]')
        return Array.isArray(parsed) ? parsed.map(value => String(value)).filter(Boolean) : []
    }
    catch {
        return []
    }
}

const savePickerFavorites = favoriteIds => {
    if (typeof window === 'undefined')
        return

    try {
        window.localStorage.setItem(PICKER_FAVORITES_KEY, JSON.stringify(favoriteIds))
    }
    catch { }
}

const createFacilityState = () => ({
    enabled: false,
    recipeIds: []
})

const createKingdomState = defaults => ({
    resourceSpaceSpecial: defaults.resourceSpaceSpecial,
    keepMinimumResources: defaults.keepMinimumResources,
    facilities: Object.fromEntries(
        defaults.facilityKeys.map(facilityKey => [facilityKey, createFacilityState()])
    )
})

const LOCATION_DEFINITIONS = [
    {
        key: 'main',
        title: 'Main Kingdom',
        shortLabel: 'Main',
        resourceSpaceSpecial: '495000',
        keepMinimumResources: '100000',
        facilityKeys: ['refinery', 'toolsmith', 'dragonHoard', 'dragonBreathForge']
    },
    {
        key: 'ice',
        title: 'Ice Kingdom',
        shortLabel: 'Ice',
        resourceSpaceSpecial: '304000',
        keepMinimumResources: '100000',
        facilityKeys: ['refinery', 'toolsmith']
    },
    {
        key: 'desert',
        title: 'Desert Kingdom',
        shortLabel: 'Desert',
        resourceSpaceSpecial: '304000',
        keepMinimumResources: '100000',
        facilityKeys: ['refinery', 'toolsmith']
    },
    {
        key: 'fire',
        title: 'Fire Kingdom',
        shortLabel: 'Fire',
        resourceSpaceSpecial: '304000',
        keepMinimumResources: '100000',
        facilityKeys: ['refinery', 'toolsmith']
    }
]

const FACILITY_DEFINITIONS = Object.freeze({
    refinery: {
        key: 'refinery',
        label: 'Refinery',
        pickerLabel: 'Refinery',
        pickerIcon: Inventory2RoundedIcon,
        iconUrl: RESOURCE_ICON_URLS.refinery,
        chip: 'refinery',
        helper: 'Numeric crafting recipe IDs for Refinery (queue 1)'
    },
    toolsmith: {
        key: 'toolsmith',
        label: 'Toolsmith',
        pickerLabel: 'Toolsmith',
        pickerIcon: HandymanRoundedIcon,
        iconUrl: RESOURCE_ICON_URLS.toolsmith,
        chip: 'toolsmith',
        helper: 'Numeric crafting recipe IDs for Toolsmith (queue 2)'
    },
    dragonHoard: {
        key: 'dragonHoard',
        label: 'DragonHoard',
        pickerLabel: 'Dragon Hoard',
        pickerIcon: ShieldRoundedIcon,
        iconUrl: RESOURCE_ICON_URLS.dragonHoard,
        chip: 'dragon-hoard',
        helper: 'Numeric crafting recipe IDs for DragonHoard (queue 3)'
    },
    dragonBreathForge: {
        key: 'dragonBreathForge',
        label: 'DragonBreathForge',
        pickerLabel: 'Dragon Breath Forge',
        pickerIcon: LocalFireDepartmentRoundedIcon,
        iconUrl: RESOURCE_ICON_URLS.dragonBreathForge,
        chip: 'dragon-breath-forge',
        helper: 'Numeric crafting recipe IDs for DragonBreathForge (queue 4)'
    }
})

const DEFAULT_PRODUCE_COMPONENTS_CONFIG = {
    checkIntervalMinutes: '65',
    minimumSendAmount: '50000',
    skipResourceLimit: '100000',
    skipToolsmithWithRubies: false,
    skipRefineryWithRubies: false,
    buyProductionSlots: false,
    resourceSendTimeSkipType: '30',
    resourceSendTimeSkipAmount: '2',
    kingdoms: Object.fromEntries(
        LOCATION_DEFINITIONS.map(location => [location.key, createKingdomState(location)])
    )
}

const getFirstDefinedValue = (...values) =>
    values.find(value => ![undefined, null, ''].includes(value))

const getLegacyProduceComponentsFallback = userPlugins => {
    const componentsPlugin = userPlugins?.auto_craft_components
    const refineryPlugin = userPlugins?.auto_craft_refinery

    return {
        checkIntervalMinutes: toStringValue(
            getFirstDefinedValue(
                componentsPlugin?.checkIntervalMinutes,
                refineryPlugin?.checkIntervalMinutes
            ),
            DEFAULT_PRODUCE_COMPONENTS_CONFIG.checkIntervalMinutes
        ),
        skipToolsmithWithRubies: Boolean(componentsPlugin?.skipWithRubies),
        skipRefineryWithRubies: Boolean(refineryPlugin?.skipWithRubies),
        buyProductionSlots: Boolean(componentsPlugin?.buyTempSlots || refineryPlugin?.buyTempSlots)
    }
}

const PRIMARY_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: 'rgba(23, 34, 55, 0.72)'
    },
    '& .MuiInputBase-input': {
        fontSize: '1.01rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

const SECTION_SX = {
    borderRadius: '22px',
    border: '1px solid rgba(129, 146, 178, 0.18)',
    background: 'linear-gradient(180deg, rgba(21, 33, 55, 0.62) 0%, rgba(18, 29, 48, 0.5) 100%)',
    boxShadow: '0 14px 32px rgba(7, 13, 26, 0.18)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(180deg, rgba(92, 118, 163, 0.07) 0%, rgba(92, 118, 163, 0) 22%)',
        pointerEvents: 'none'
    }
}

const SUBSECTION_SX = {
    borderRadius: '18px',
    border: '1px solid rgba(129, 146, 178, 0.14)',
    backgroundColor: 'rgba(16, 25, 41, 0.44)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.02)'
}

const RECIPE_CARD_SX = {
    borderRadius: '10px',
    backgroundColor: 'rgba(70, 86, 116, 0.35)',
    border: '1px solid rgba(129, 146, 178, 0.12)'
}

const SKIP_TIME_OPTIONS = [
    { value: '1', label: '1 Minute' },
    { value: '5', label: '5 Minutes' },
    { value: '10', label: '10 Minutes' },
    { value: '30', label: '30 Minutes' },
    { value: '60', label: '60 Minutes' },
    { value: '300', label: '5 Hours' },
    { value: '1440', label: '24 Hours' }
]

const makeRecipeRangeEntries = ({
    facilityKey,
    chip,
    itemName,
    startId,
    count,
    type,
    levelStart = 1
}) => {
    const typeLabel =
        type === 'Short' ? 'Short Manual' :
            type === 'Long' ? 'Long Manual' :
                'Ruby Manual'

    return Array.from({ length: count }, (_, index) => {
        const level = levelStart + index
        const recipeId = startId + index
        const lowerItemName = itemName.toLowerCase()
        const lowerTypeLabel = typeLabel.toLowerCase()
        const iconUrl = RECIPE_ICON_URL_BY_ITEM[itemName] ?? FACILITY_DEFINITIONS[facilityKey]?.iconUrl ?? ''

        return {
            facilityKey,
            recipeId: String(recipeId),
            recipeType: type,
            label: `${recipeId}: ${itemName} ${typeLabel} (Level ${level})`,
            iconUrl,
            chip,
            description: `${lowerTypeLabel} for ${lowerItemName} production (Level ${level})`,
            searchText: `${itemName} ${typeLabel} ${recipeId} level ${level} ${chip}`
        }
    })
}

const RECIPE_CATALOG = [
    ...makeRecipeRangeEntries({ facilityKey: 'refinery', chip: 'refinery', itemName: 'Refined wood', startId: 1, count: 11, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'refinery', chip: 'refinery', itemName: 'Refined wood', startId: 12, count: 11, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'refinery', chip: 'refinery', itemName: 'Refined stone', startId: 34, count: 11, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'refinery', chip: 'refinery', itemName: 'Refined stone', startId: 45, count: 11, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Screw', startId: 67, count: 11, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Screw', startId: 78, count: 11, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Black Powder', startId: 100, count: 11, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Black Powder', startId: 111, count: 11, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Saw', startId: 133, count: 11, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Saw', startId: 144, count: 11, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Drill', startId: 166, count: 11, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Drill', startId: 177, count: 11, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Crowbar', startId: 199, count: 11, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Crowbar', startId: 210, count: 11, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Leather Strip', startId: 232, count: 11, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Leather Strip', startId: 243, count: 11, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Chain', startId: 265, count: 11, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Chain', startId: 276, count: 11, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Metal Plate', startId: 298, count: 11, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'toolsmith', chip: 'toolsmith', itemName: 'Metal Plate', startId: 309, count: 11, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'dragonHoard', chip: 'dragon-hoard', itemName: 'Dragon Scale Tile', startId: 331, count: 7, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'dragonHoard', chip: 'dragon-hoard', itemName: 'Dragon Scale Tile', startId: 338, count: 10, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'dragonHoard', chip: 'dragon-hoard', itemName: 'Dragon Charm', startId: 348, count: 10, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'dragonHoard', chip: 'dragon-hoard', itemName: 'Dragon Charm', startId: 358, count: 10, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'refinery', chip: 'refinery', itemName: 'Iron', startId: 368, count: 10, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'refinery', chip: 'refinery', itemName: 'Iron', startId: 378, count: 10, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'refinery', chip: 'refinery', itemName: 'Glass', startId: 398, count: 10, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'refinery', chip: 'refinery', itemName: 'Glass', startId: 408, count: 10, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'dragonBreathForge', chip: 'dragon-breath-forge', itemName: 'Twin Flame Axes', startId: 428, count: 10, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'dragonBreathForge', chip: 'dragon-breath-forge', itemName: 'Twin Flame Axes', startId: 438, count: 10, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'dragonBreathForge', chip: 'dragon-breath-forge', itemName: 'Dragon Glass Arrows', startId: 448, count: 10, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'dragonBreathForge', chip: 'dragon-breath-forge', itemName: 'Dragon Glass Arrows', startId: 458, count: 10, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'dragonBreathForge', chip: 'dragon-breath-forge', itemName: 'Dragon Scale Armor', startId: 468, count: 10, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'dragonBreathForge', chip: 'dragon-breath-forge', itemName: 'Dragon Scale Armor', startId: 478, count: 10, type: 'Long' }),
    ...makeRecipeRangeEntries({ facilityKey: 'dragonBreathForge', chip: 'dragon-breath-forge', itemName: 'Dragon Scale Arrows', startId: 488, count: 10, type: 'Short' }),
    ...makeRecipeRangeEntries({ facilityKey: 'dragonBreathForge', chip: 'dragon-breath-forge', itemName: 'Dragon Scale Arrows', startId: 498, count: 10, type: 'Long' })
]

const RECIPE_BY_ID = Object.fromEntries(RECIPE_CATALOG.map(recipe => [recipe.recipeId, recipe]))

const normalizeRecipeIds = (facilityKey, value) =>
    Array.isArray(value) ? value
        .map(entry => {
            if (entry && typeof entry === 'object') {
                const recipeId = toStringValue(entry.recipeId ?? entry.id).trim()
                if (!recipeId)
                    return {
                        facilityKey,
                        recipeId: '',
                        label: '',
                        iconUrl: '',
                        chip: toStringValue(entry.chip, FACILITY_DEFINITIONS[facilityKey]?.chip ?? ''),
                        description: toStringValue(entry.description)
                    }

                const recipe = RECIPE_BY_ID[recipeId]
                return recipe ? { ...recipe } : {
                    facilityKey,
                    recipeId,
                    label: toStringValue(entry.label, recipeId),
                    iconUrl: toStringValue(entry.iconUrl),
                    chip: toStringValue(entry.chip, FACILITY_DEFINITIONS[facilityKey]?.chip ?? ''),
                    description: toStringValue(entry.description)
                }
            }

            const recipeId = toStringValue(entry).trim()
            if (!recipeId)
                return {
                    facilityKey,
                    recipeId: '',
                    label: '',
                    iconUrl: '',
                    chip: FACILITY_DEFINITIONS[facilityKey]?.chip ?? '',
                    description: ''
                }

            return RECIPE_BY_ID[recipeId] ? { ...RECIPE_BY_ID[recipeId] } : null
        })
        .filter(Boolean) :
        []

const normalizeFacilityState = (facilityKey, value) => ({
    enabled: Boolean(value?.enabled),
    recipeIds: normalizeRecipeIds(facilityKey, value?.recipeIds)
})

const normalizeKingdomState = (location, value) => ({
    resourceSpaceSpecial: toStringValue(value?.resourceSpaceSpecial, location.resourceSpaceSpecial),
    keepMinimumResources: toStringValue(value?.keepMinimumResources, location.keepMinimumResources),
    facilities: Object.fromEntries(
        location.facilityKeys.map(facilityKey => [
            facilityKey,
            normalizeFacilityState(facilityKey, value?.facilities?.[facilityKey])
        ])
    )
})

export const normalizeProduceComponentsConfig = (value, fallback = {}) => {
    const source = value && typeof value === 'object' ? value : {}

    return {
        checkIntervalMinutes: toStringValue(
            source.checkIntervalMinutes,
            toStringValue(fallback.checkIntervalMinutes, DEFAULT_PRODUCE_COMPONENTS_CONFIG.checkIntervalMinutes)
        ),
        minimumSendAmount: toStringValue(source.minimumSendAmount, DEFAULT_PRODUCE_COMPONENTS_CONFIG.minimumSendAmount),
        skipResourceLimit: toStringValue(source.skipResourceLimit, DEFAULT_PRODUCE_COMPONENTS_CONFIG.skipResourceLimit),
        skipToolsmithWithRubies:
            source.skipToolsmithWithRubies === undefined ?
                Boolean(fallback.skipToolsmithWithRubies) :
                Boolean(source.skipToolsmithWithRubies),
        skipRefineryWithRubies:
            source.skipRefineryWithRubies === undefined ?
                Boolean(fallback.skipRefineryWithRubies) :
                Boolean(source.skipRefineryWithRubies),
        buyProductionSlots:
            source.buyProductionSlots === undefined ?
                Boolean(fallback.buyProductionSlots) :
                Boolean(source.buyProductionSlots),
        resourceSendTimeSkipType: toStringValue(source.resourceSendTimeSkipType, DEFAULT_PRODUCE_COMPONENTS_CONFIG.resourceSendTimeSkipType),
        resourceSendTimeSkipAmount: toStringValue(source.resourceSendTimeSkipAmount, DEFAULT_PRODUCE_COMPONENTS_CONFIG.resourceSendTimeSkipAmount),
        kingdoms: Object.fromEntries(
            LOCATION_DEFINITIONS.map(location => [
                location.key,
                normalizeKingdomState(location, source?.kingdoms?.[location.key])
            ])
        )
    }
}

export const resolveProduceComponentsConfig = userPlugins =>
    normalizeProduceComponentsConfig(
        userPlugins?.auto_craft_components?.produceComponentsConfig ??
        userPlugins?.auto_craft_refinery?.produceComponentsConfig,
        getLegacyProduceComponentsFallback(userPlugins)
    )

function HelperText({ children, sx }) {
    return (
        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.7, ...sx }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: '#95a5c4' }} />
            <Typography sx={{ fontSize: '0.89rem', color: '#95a5c4', lineHeight: 1.35 }}>
                {children}
            </Typography>
        </Stack>
    )
}

function SettingSwitch({ checked, onChange }) {
    return (
        <Switch
            checked={checked}
            onChange={event => onChange(event.target.checked)}
            sx={{
                width: 48,
                height: 29,
                p: 0,
                '& .MuiSwitch-switchBase': {
                    p: '2.5px',
                    '&.Mui-checked': {
                        transform: 'translateX(18px)',
                        color: '#ffffff'
                    },
                    '&.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#2f7cff',
                        opacity: 1
                    }
                },
                '& .MuiSwitch-thumb': {
                    width: 24,
                    height: 24,
                    boxShadow: 'none'
                },
                '& .MuiSwitch-track': {
                    opacity: 1,
                    borderRadius: '999px',
                    backgroundColor: 'rgba(111, 123, 143, 0.9)'
                }
            }}
        />
    )
}

function SectionHeading({ children }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1.3} sx={{ mb: 2.2 }}>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1.05rem', fontWeight: 800 }}>
                {children}
            </Typography>
            <Divider sx={{ flex: 1, borderColor: 'rgba(129, 146, 178, 0.22)' }} />
        </Stack>
    )
}

function RecipePickerDialog({ open, facilityKey, onClose, onSelect }) {
    const [searchTerm, setSearchTerm] = React.useState('')
    const [favoritesOnly, setFavoritesOnly] = React.useState(false)
    const [activeFilter, setActiveFilter] = React.useState('facility')
    const [favoriteIds, setFavoriteIds] = React.useState(loadPickerFavorites)
    const deferredSearchTerm = React.useDeferredValue(searchTerm)
    const facility = FACILITY_DEFINITIONS[facilityKey]
    const FacilityIcon = facility?.pickerIcon ?? Inventory2RoundedIcon

    React.useEffect(() => {
        if (open) {
            setSearchTerm('')
            setFavoritesOnly(false)
            setActiveFilter('facility')
        }
    }, [open, facilityKey])

    const filteredRecipes = React.useMemo(() => {
        const normalizedQuery = normalizeSearchText(deferredSearchTerm)
        let recipes = RECIPE_CATALOG.filter(recipe =>
            activeFilter === 'all' || recipe.facilityKey === facilityKey
        )

        if (favoritesOnly)
            recipes = recipes.filter(recipe => favoriteIds.includes(recipe.recipeId))

        if (normalizedQuery)
            recipes = recipes.filter(recipe => normalizeSearchText(recipe.searchText).includes(normalizedQuery))

        return recipes
    }, [activeFilter, deferredSearchTerm, facilityKey, favoriteIds, favoritesOnly])

    React.useEffect(() => {
        savePickerFavorites(favoriteIds)
    }, [favoriteIds])

    const totalCount = RECIPE_CATALOG.length
    const facilityCount = RECIPE_CATALOG.filter(recipe => recipe.facilityKey === facilityKey).length

    const toggleFavorite = recipeId => {
        setFavoriteIds(previous =>
            previous.includes(recipeId) ?
                previous.filter(currentId => currentId !== recipeId) :
                [...previous, recipeId]
        )
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: 'min(calc(100vw - 40px), 760px)',
                    maxWidth: '760px',
                    borderRadius: '18px',
                    background: 'linear-gradient(180deg, rgba(31,44,70,0.98) 0%, rgba(24,36,59,0.98) 100%)',
                    border: '1px solid rgba(255,255,255,0.08)'
                }
            }}
            BackdropProps={{
                sx: {
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(4, 10, 20, 0.62)'
                }
            }}
        >
            <DialogTitle sx={{ px: 2.5, pt: 2.2, pb: 1.2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                        <SearchRoundedIcon sx={{ color: '#f3f7ff', fontSize: 28 }} />
                        <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#f3f7ff' }}>
                            Resource Picker
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose} sx={{ color: '#a9b4ca' }}>
                        <CloseRoundedIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ px: 2.5, pb: 2.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mb: 1.6 }}>
                    <TextField
                        fullWidth
                        placeholder="Search by name or ID..."
                        value={searchTerm}
                        onChange={event => setSearchTerm(event.target.value)}
                        sx={PRIMARY_INPUT_SX}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon sx={{ color: '#a9b4ca' }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        variant="outlined"
                        onClick={() => setFavoritesOnly(previous => !previous)}
                        sx={{
                            minWidth: 116,
                            borderRadius: 2.5,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderColor: favoritesOnly ? 'rgba(67, 128, 255, 0.6)' : 'rgba(129, 146, 178, 0.22)',
                            backgroundColor: favoritesOnly ? 'rgba(67, 128, 255, 0.14)' : 'transparent'
                        }}
                        startIcon={favoritesOnly ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
                    >
                        Favorites
                    </Button>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.9, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        onClick={() => setActiveFilter('all')}
                        startIcon={<FilterAltRoundedIcon />}
                        sx={{
                            borderRadius: 2.5,
                            textTransform: 'none',
                            fontWeight: 800,
                            color: '#f5f7ff',
                            borderColor: activeFilter === 'all' ? 'rgba(67, 128, 255, 0.6)' : 'rgba(129, 146, 178, 0.22)',
                            backgroundColor: activeFilter === 'all' ? 'rgba(67, 128, 255, 0.14)' : 'rgba(255,255,255,0.03)'
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <span>All Resources</span>
                            <Box sx={{ px: 0.9, py: 0.2, borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.08)', fontSize: '0.82rem' }}>
                                {totalCount}
                            </Box>
                        </Stack>
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setActiveFilter('facility')}
                        startIcon={<FacilityIcon />}
                        sx={{
                            borderRadius: 2.5,
                            textTransform: 'none',
                            fontWeight: 800,
                            color: '#f5f7ff',
                            borderColor: activeFilter === 'facility' ? 'rgba(67, 128, 255, 0.6)' : 'rgba(129, 146, 178, 0.22)',
                            backgroundColor: activeFilter === 'facility' ? 'rgba(67, 128, 255, 0.14)' : 'rgba(255,255,255,0.03)'
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <span>{facility?.pickerLabel ?? 'Category'}</span>
                            <Box sx={{ px: 0.9, py: 0.2, borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.08)', fontSize: '0.82rem' }}>
                                {facilityCount}
                            </Box>
                        </Stack>
                    </Button>
                </Stack>

                <Typography sx={{ color: '#96a5c0', fontSize: '0.92rem', mb: 1.4 }}>
                    {`${filteredRecipes.length} results`}
                </Typography>

                <Box
                    sx={{
                        maxHeight: 'min(58vh, 560px)',
                        overflowY: 'auto',
                        pr: 0.5,
                        borderRadius: '14px',
                        border: '1px solid rgba(129, 146, 178, 0.18)',
                        backgroundColor: 'rgba(16, 25, 41, 0.48)',
                        p: 1.2
                    }}
                >
                    {filteredRecipes.length ? (
                        <Stack spacing={1.1}>
                            {filteredRecipes.map(recipe => (
                                <Box
                                    key={recipe.recipeId}
                                    onClick={() => onSelect(recipe)}
                                    sx={{
                                        p: 1.6,
                                        borderRadius: '12px',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        backgroundColor: 'rgba(18, 28, 47, 0.76)',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'rgba(28, 42, 67, 0.9)',
                                            borderColor: 'rgba(67, 128, 255, 0.55)'
                                        }
                                    }}
                                >
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                                        <UnitIconFrame iconUrl={recipe.iconUrl || facility?.iconUrl} itemType="resource" size={100} />
                                        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography sx={{ color: '#56a0ff', fontSize: '0.95rem', fontWeight: 700 }}>
                                                    {`#${recipe.recipeId}`}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        px: 1,
                                                        py: 0.35,
                                                        borderRadius: 999,
                                                        backgroundColor: 'rgba(84, 100, 131, 0.3)',
                                                        border: '1px solid rgba(147, 162, 188, 0.2)',
                                                        color: '#dce6ff',
                                                        fontSize: '0.82rem',
                                                        fontWeight: 700,
                                                        textTransform: 'lowercase'
                                                    }}
                                                >
                                                    {recipe.chip}
                                                </Box>
                                            </Stack>
                                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                                                {recipe.label.replace(/^[0-9]+:\s*/, '')}
                                            </Typography>
                                            <Typography sx={{ color: '#96a5c0', fontSize: '0.95rem' }}>
                                                {recipe.description}
                                            </Typography>
                                        </Stack>
                                        <IconButton
                                            onClick={event => {
                                                event.stopPropagation()
                                                toggleFavorite(recipe.recipeId)
                                            }}
                                            sx={{ color: favoriteIds.includes(recipe.recipeId) ? '#ff6b76' : '#a9b4ca' }}
                                        >
                                            {favoriteIds.includes(recipe.recipeId) ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
                                        </IconButton>
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    ) : (
                        <Box
                            sx={{
                                py: 6,
                                textAlign: 'center',
                                color: '#96a5c0',
                                fontSize: '0.98rem'
                            }}
                        >
                            No resources match the current search.
                        </Box>
                    )}
                </Box>

                <Typography sx={{ color: '#7d8aa4', fontSize: '0.88rem', textAlign: 'center', mt: 1.5 }}>
                    Click to select. Use
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            mx: 0.5,
                            color: '#ff5f70',
                            verticalAlign: 'middle'
                        }}
                    >
                        <FavoriteRoundedIcon sx={{ fontSize: 14 }} />
                    </Box>
                    to favorite. Type ID manually in the input field.
                </Typography>
            </DialogContent>
        </Dialog>
    )
}

function RecipeRow({ recipe, onPick, onRemove, onManualChange }) {
    return (
        <Box sx={{ ...RECIPE_CARD_SX, p: 1.1 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <UnitIconFrame iconUrl={recipe?.iconUrl} itemType="resource" size={100} />
                <TextField
                    fullWidth
                    value={recipe?.label || recipe?.recipeId || ''}
                    placeholder="Select recipe ID"
                    onChange={event => onManualChange(event.target.value)}
                    sx={PRIMARY_INPUT_SX}
                />
                <IconButton
                    onClick={onPick}
                    sx={{
                        mt: 0.15,
                        width: 44,
                        height: 44,
                        borderRadius: '10px',
                        border: '1px solid rgba(129, 146, 178, 0.22)',
                        color: '#f5f7ff'
                    }}
                >
                    <SearchRoundedIcon />
                </IconButton>
                <IconButton
                    onClick={onRemove}
                    sx={{
                        mt: 0.15,
                        width: 44,
                        height: 44,
                        borderRadius: '10px',
                        backgroundColor: '#ff3646',
                        color: '#ffffff',
                        '&:hover': {
                            backgroundColor: '#ff2234'
                        }
                    }}
                >
                    <RemoveRoundedIcon />
                </IconButton>
            </Stack>

            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mt: 1 }}>
                <Box
                    sx={{
                        px: 1,
                        py: 0.35,
                        borderRadius: 999,
                        backgroundColor: 'rgba(84, 100, 131, 0.48)',
                        border: '1px solid rgba(147, 162, 188, 0.2)',
                        color: '#dce6ff',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        textTransform: 'lowercase'
                    }}
                >
                    {recipe?.chip ?? ''}
                </Box>
                <Typography sx={{ color: '#96a5c0', fontSize: '0.9rem', lineHeight: 1.35 }}>
                    {recipe?.description ?? ''}
                </Typography>
            </Stack>
        </Box>
    )
}

function RecipeListEditor({ titlePrefix, facilityKey, value, onChange }) {
    const [pickerState, setPickerState] = React.useState({ open: false, index: -1 })
    const facility = FACILITY_DEFINITIONS[facilityKey]
    const recipes = Array.isArray(value) ? value : []

    const handleOpenPicker = index => setPickerState({ open: true, index })
    const handleClosePicker = () => setPickerState({ open: false, index: -1 })

    const handleAdd = () => {
        const nextRecipes = [...recipes, { recipeId: '', label: '', iconUrl: '', chip: facility.chip, description: '' }]
        onChange(nextRecipes)
        setPickerState({ open: true, index: nextRecipes.length - 1 })
    }

    const handleSelectRecipe = recipe => {
        const nextRecipes = [...recipes]
        nextRecipes[pickerState.index] = { ...recipe }
        onChange(nextRecipes)
        handleClosePicker()
    }

    const handleRemove = index => {
        const nextRecipes = recipes.filter((_, recipeIndex) => recipeIndex !== index)
        onChange(nextRecipes)
    }

    const handleManualRecipeChange = (index, rawValue) => {
        const manualId = String(rawValue ?? '').trim().replace(/^#/, '')
        const nextRecipes = [...recipes]

        if (!manualId) {
            nextRecipes[index] = { recipeId: '', label: '', chip: facility.chip, description: '' }
            onChange(nextRecipes)
            return
        }

        const selectedRecipe = RECIPE_BY_ID[manualId]
        if (selectedRecipe && selectedRecipe.facilityKey === facilityKey) {
            nextRecipes[index] = { ...selectedRecipe }
        }
        else {
            nextRecipes[index] = {
                facilityKey,
                recipeId: manualId,
                label: manualId,
                iconUrl: '',
                chip: facility.chip,
                description: 'Type a valid recipe ID or use the search icon'
            }
        }

        onChange(nextRecipes)
    }

    return (
        <Stack spacing={1.15}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Box>
                    <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                        {`${titlePrefix}: ${facility.label} Recipe IDs`}
                    </Typography>
                    <Typography sx={{ color: '#f5f7ff', fontSize: '0.98rem', fontWeight: 700, mt: 0.65 }}>
                        {`${titlePrefix}: ${facility.label} Recipe IDs (${recipes.length} items)`}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleAdd}
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        border: '1px solid rgba(129, 146, 178, 0.22)',
                        color: '#f5f7ff'
                    }}
                >
                    <AddRoundedIcon />
                </IconButton>
            </Stack>

            <Stack spacing={1}>
                {recipes.map((recipe, index) => (
                    <RecipeRow
                        key={`${recipe?.recipeId ?? 'recipe'}-${index}`}
                        recipe={recipe}
                        onPick={() => handleOpenPicker(index)}
                        onRemove={() => handleRemove(index)}
                        onManualChange={value => handleManualRecipeChange(index, value)}
                    />
                ))}
            </Stack>

            <HelperText>{facility.helper}</HelperText>

            <RecipePickerDialog
                open={pickerState.open}
                facilityKey={facilityKey}
                onClose={handleClosePicker}
                onSelect={handleSelectRecipe}
            />
        </Stack>
    )
}

export const buildProduceComponentsPluginState = (userPlugins, nextConfig) => {
    const nextPlugins = cloneValue(userPlugins ?? {})
    const normalizedConfig = normalizeProduceComponentsConfig(nextConfig)
    const hasActiveFacilities = Object.values(normalizedConfig?.kingdoms ?? {}).some(location =>
        Object.values(location?.facilities ?? {}).some(facility =>
            Boolean(facility?.enabled) &&
            Array.isArray(facility?.recipeIds) &&
            facility.recipeIds.some(recipe =>
                String(recipe?.recipeId ?? recipe?.id ?? recipe ?? '').trim())
        )
    )

    for (const pluginKey of PRODUCE_COMPONENTS_PLUGIN_KEYS) {
        nextPlugins[pluginKey] ??= {}
        nextPlugins[pluginKey].state = hasActiveFacilities
        nextPlugins[pluginKey].produceComponentsConfig = normalizedConfig
        nextPlugins[pluginKey].checkIntervalMinutes = Number(normalizedConfig.checkIntervalMinutes) || 65
        nextPlugins[pluginKey].buyTempSlots = Boolean(normalizedConfig.buyProductionSlots)
    }

    nextPlugins.auto_craft_components.skipWithRubies = Boolean(normalizedConfig.skipToolsmithWithRubies)
    nextPlugins.auto_craft_refinery.skipWithRubies = Boolean(normalizedConfig.skipRefineryWithRubies)

    return nextPlugins
}

export default function ProduceComponentsSettingsPanel({ userPlugins, onChange }) {
    const activeConfig = React.useMemo(
        () => resolveProduceComponentsConfig(userPlugins),
        [userPlugins]
    )

    const commitConfig = React.useCallback(nextConfig => {
        onChange(buildProduceComponentsPluginState(userPlugins, nextConfig))
    }, [onChange, userPlugins])

    const updateConfig = updater => {
        const nextConfig = cloneValue(activeConfig)
        updater(nextConfig)
        commitConfig(nextConfig)
    }

    const updateGeneralField = (field, value) => updateConfig(nextConfig => {
        nextConfig[field] = value
    })

    const updateKingdomField = (kingdomKey, field, value) => updateConfig(nextConfig => {
        nextConfig.kingdoms[kingdomKey][field] = value
    })

    const updateFacilityEnabled = (kingdomKey, facilityKey, value) => updateConfig(nextConfig => {
        nextConfig.kingdoms[kingdomKey].facilities[facilityKey].enabled = value
    })

    const updateFacilityRecipes = (kingdomKey, facilityKey, recipeIds) => updateConfig(nextConfig => {
        nextConfig.kingdoms[kingdomKey].facilities[facilityKey].recipeIds = recipeIds
    })

    return (
        <Stack spacing={3}>
            <Box sx={{ ...SECTION_SX, p: { xs: 2.2, md: 3 } }}>
                <Stack spacing={0.75} sx={{ mb: 2.4 }}>
                    <Stack direction="row" spacing={1.1} alignItems="center">
                        <Inventory2RoundedIcon sx={{ color: '#f5f7ff', fontSize: 22 }} />
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1.3rem', fontWeight: 800 }}>
                            Produce Components Settings
                        </Typography>
                    </Stack>
                    <Typography sx={{ color: '#96a5c0', fontSize: '1rem', maxWidth: 960 }}>
                        Configure recipe-list based production for Refinery, Toolsmith, DragonHoard, and DragonBreathForge
                    </Typography>
                </Stack>

                <SectionHeading>General Settings</SectionHeading>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
                        gap: 2.4
                    }}
                >
                    <Box>
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                            Check Interval (Minutes)
                        </Typography>
                        <TextField
                            fullWidth
                            value={activeConfig.checkIntervalMinutes}
                            onChange={event => updateGeneralField('checkIntervalMinutes', event.target.value)}
                            sx={PRIMARY_INPUT_SX}
                        />
                        <HelperText>How often to run Produce Components</HelperText>
                    </Box>

                    <Box>
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                            Minimum Send Amount
                        </Typography>
                        <TextField
                            fullWidth
                            value={activeConfig.minimumSendAmount}
                            onChange={event => updateGeneralField('minimumSendAmount', event.target.value)}
                            sx={PRIMARY_INPUT_SX}
                        />
                        <HelperText>Minimum amount of resources to transfer between kingdoms</HelperText>
                    </Box>

                    <Box>
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                            Skip Resource Limit
                        </Typography>
                        <TextField
                            fullWidth
                            value={activeConfig.skipResourceLimit}
                            onChange={event => updateGeneralField('skipResourceLimit', event.target.value)}
                            sx={PRIMARY_INPUT_SX}
                        />
                        <HelperText>Required resources to allow ruby skip actions</HelperText>
                    </Box>

                    <Box>
                        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ pt: 1.15 }}>
                            <SettingSwitch
                                checked={activeConfig.skipToolsmithWithRubies}
                                onChange={value => updateGeneralField('skipToolsmithWithRubies', value)}
                            />
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                                Skip Toolsmith With Rubies
                            </Typography>
                        </Stack>
                        <HelperText>Use rubies to skip Toolsmith queue in Main when limit is met</HelperText>
                    </Box>

                    <Box>
                        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ pt: 1.15 }}>
                            <SettingSwitch
                                checked={activeConfig.skipRefineryWithRubies}
                                onChange={value => updateGeneralField('skipRefineryWithRubies', value)}
                            />
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                                Skip Refinery With Rubies
                            </Typography>
                        </Stack>
                        <HelperText>Use rubies to skip Refinery queue in Main when limit is met</HelperText>
                    </Box>

                    <Box>
                        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ pt: 1.15 }}>
                            <SettingSwitch
                                checked={activeConfig.buyProductionSlots}
                                onChange={value => updateGeneralField('buyProductionSlots', value)}
                            />
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                                Buy Production Slots
                            </Typography>
                        </Stack>
                        <HelperText>Automatically buy missing queue/production slots in configured buildings</HelperText>
                    </Box>

                    <Box>
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                            Resource Send Time Skip Type
                        </Typography>
                        <TextField
                            select
                            value={activeConfig.resourceSendTimeSkipType}
                            onChange={event => updateGeneralField('resourceSendTimeSkipType', event.target.value)}
                            sx={{ ...PRIMARY_INPUT_SX, maxWidth: 270 }}
                        >
                            {SKIP_TIME_OPTIONS.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <HelperText>Type of time skip used when skipping resource transport</HelperText>
                    </Box>

                    <Box>
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                            Resource Send Time Skip Amount
                        </Typography>
                        <TextField
                            fullWidth
                            value={activeConfig.resourceSendTimeSkipAmount}
                            onChange={event => updateGeneralField('resourceSendTimeSkipAmount', event.target.value)}
                            sx={PRIMARY_INPUT_SX}
                        />
                        <HelperText>Amount of time skip items used for resource transport skips</HelperText>
                    </Box>
                </Box>
            </Box>

            {LOCATION_DEFINITIONS.map(location => {
                const kingdomState = activeConfig.kingdoms[location.key]

                return (
                    <Box key={location.key} sx={{ ...SECTION_SX, p: { xs: 2.2, md: 3 } }}>
                        <SectionHeading>{location.title}</SectionHeading>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
                                gap: 2.2
                            }}
                        >
                            <Box>
                                <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                                    {`${location.shortLabel}: Resource Space Special`}
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={kingdomState.resourceSpaceSpecial}
                                    onChange={event => updateKingdomField(location.key, 'resourceSpaceSpecial', event.target.value)}
                                    sx={PRIMARY_INPUT_SX}
                                />
                            </Box>

                            <Box>
                                <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                                    {`${location.shortLabel}: Keep Minimum Resources`}
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={kingdomState.keepMinimumResources}
                                    onChange={event => updateKingdomField(location.key, 'keepMinimumResources', event.target.value)}
                                    sx={PRIMARY_INPUT_SX}
                                />
                            </Box>
                        </Box>

                        <Stack spacing={2.3} sx={{ mt: 3 }}>
                            {location.facilityKeys.map(facilityKey => {
                                const facility = FACILITY_DEFINITIONS[facilityKey]
                                const facilityState = kingdomState.facilities[facilityKey]

                                return (
                                    <Box key={`${location.key}-${facilityKey}`} sx={{ ...SUBSECTION_SX, p: 2 }}>
                                        <Stack direction="row" alignItems="center" spacing={1.4} sx={{ mb: 2.2 }}>
                                            <UnitIconFrame iconUrl={facility.iconUrl} itemType="building" size={100} />
                                            <Typography sx={{ color: '#f5f7ff', fontSize: '1.05rem', fontWeight: 800 }}>
                                                {`${location.shortLabel}: ${facility.label}`}
                                            </Typography>
                                            <Divider sx={{ flex: 1, borderColor: 'rgba(129, 146, 178, 0.22)' }} />
                                        </Stack>

                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr', xl: '0.8fr 1.2fr' },
                                                gap: 2.2
                                            }}
                                        >
                                            <Box>
                                                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ pt: 0.35 }}>
                                                    <SettingSwitch
                                                        checked={facilityState.enabled}
                                                        onChange={value => updateFacilityEnabled(location.key, facilityKey, value)}
                                                    />
                                                    <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                                                        {`${location.shortLabel}: Enable ${facility.label}`}
                                                    </Typography>
                                                </Stack>
                                                <HelperText>
                                                    {facilityKey === 'dragonHoard' || facilityKey === 'dragonBreathForge' ?
                                                        `${facility.label} only exists in KID 0 (Main)` :
                                                        `Enable ${facility.label} crafting in ${location.title}`}
                                                </HelperText>
                                            </Box>

                                            <RecipeListEditor
                                                titlePrefix={location.shortLabel}
                                                facilityKey={facilityKey}
                                                value={facilityState.recipeIds}
                                                onChange={value => updateFacilityRecipes(location.key, facilityKey, value)}
                                            />
                                        </Box>
                                    </Box>
                                )
                            })}
                        </Stack>
                    </Box>
                )
            })}
        </Stack>
    )
}
